import { createFileRoute } from "@tanstack/react-router";
import {
  CheckCircle2,
  AlertTriangle,
  Shield,
  Clock,
  Globe,
  Mail,
  ShoppingCart,
  Activity,
  Lock,
  ArrowRight,
  ListChecks,
  XCircle,
  Copy,
} from "lucide-react";
import { LinksGrupos, GRUPOS_INSTITUCIONAIS } from "@/components/LinksOficiais";

import { AdminGate } from "@/components/admin/AdminGate";

export const Route = createFileRoute("/admin/plano-migracao")({
  head: () => ({
    meta: [
      { title: "Admin — Plano de Migração" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: () => (
    <AdminGate route="/admin/plano-migracao">
      <PlanoMigracaoPage />
    </AdminGate>
  ),
});

function Slide({
  number,
  total,
  kicker,
  title,
  children,
  tone = "default",
}: {
  number: number;
  total: number;
  kicker?: string;
  title: string;
  children: React.ReactNode;
  tone?: "default" | "warning" | "success" | "danger";
}) {
  const toneStyles = {
    default: "border-primary/20 bg-gradient-to-br from-card to-secondary/40",
    warning: "border-gold/40 bg-gradient-to-br from-gold/10 to-card",
    success: "border-success/40 bg-gradient-to-br from-success/10 to-card",
    danger: "border-destructive/40 bg-gradient-to-br from-destructive/10 to-card",
  }[tone];

  const kickerColor = {
    default: "text-primary",
    warning: "text-gold",
    success: "text-success",
    danger: "text-destructive",
  }[tone];

  return (
    <section
      className={`relative w-full overflow-hidden rounded-3xl border ${toneStyles} p-5 sm:p-6 md:p-10 shadow-card`}
    >
      <div className="flex items-center justify-between mb-6 gap-3">
        {kicker ? (
          <span className={`text-[10px] sm:text-xs md:text-sm font-bold uppercase tracking-[0.18em] ${kickerColor} break-words min-w-0`}>
            {kicker}
          </span>
        ) : (
          <span />
        )}
        <span className="text-xs md:text-sm font-mono text-muted-foreground shrink-0">
          {String(number).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </span>
      </div>
      <h2 className="text-xl sm:text-2xl md:text-4xl font-bold text-foreground mb-6 leading-tight break-words">
        {title}
      </h2>
      <div className="text-foreground/90 break-words [&_code]:break-all">{children}</div>
    </section>
  );
}

function Pill({
  children,
  tone = "default",
}: {
  children: React.ReactNode;
  tone?: "default" | "danger" | "success" | "warning";
}) {
  const styles = {
    default: "bg-primary/15 text-primary border-primary/30",
    danger: "bg-destructive/15 text-destructive border-destructive/40",
    success: "bg-success/15 text-success border-success/40",
    warning: "bg-gold/15 text-gold border-gold/40",
  }[tone];
  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs md:text-sm font-semibold ${styles}`}>
      {children}
    </span>
  );
}
function PlanoMigracaoPage() {
  const total = 14;

  return (
    <div className="min-h-screen overflow-x-hidden">


      {/* Hero */}
      <header className="relative overflow-hidden" style={{ background: "var(--gradient-hero)" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 md:py-24">
          <Pill tone="success">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Migração concluída · Junho/2026
          </Pill>
          <h1 className="mt-6 text-3xl sm:text-4xl md:text-6xl font-bold text-foreground leading-[1.05] break-words">
            Plano de Migração
            <br />
            <span className="text-primary break-all">fisioemortopedia.com.br</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-muted-foreground max-w-2xl">
            Do Wix para o Lovable, sem parar vendas, sem quebrar email, sem perder tracking.
            <strong className="text-success"> Executado com sucesso.</strong>
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            <Pill tone="success"><CheckCircle2 className="w-3.5 h-3.5" /> Domínio ativo</Pill>
            <Pill tone="success"><CheckCircle2 className="w-3.5 h-3.5" /> Tracking funcionando</Pill>
            <Pill tone="success"><CheckCircle2 className="w-3.5 h-3.5" /> Eduzz integrada</Pill>
            <Pill tone="success"><CheckCircle2 className="w-3.5 h-3.5" /> GSC ativo</Pill>
          </div>
        </div>
      </header>

      {/* Slides */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12 space-y-8">
        {/* Slide 1 — Objetivo */}
        <Slide number={1} total={total} kicker="Objetivo" title="O que vamos fazer nessa migração">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-background/40 border border-border p-5">
              <Globe className="w-6 h-6 text-primary mb-3" />
              <h3 className="font-bold mb-2">Migrar o domínio principal</h3>
              <p className="text-sm text-muted-foreground">
                <code className="text-primary">fisioemortopedia.com.br</code> sai do Wix e passa a apontar pro Lovable.
              </p>
            </div>
            <div className="rounded-2xl bg-background/40 border border-border p-5">
              <ListChecks className="w-6 h-6 text-primary mb-3" />
              <h3 className="font-bold mb-2">Concentrar páginas aqui</h3>
              <p className="text-sm text-muted-foreground">
                Institucional, congresso e capturas de lead — todas já recriadas no Lovable.
              </p>
            </div>
            <div className="rounded-2xl bg-background/40 border border-border p-5">
              <ShoppingCart className="w-6 h-6 text-gold mb-3" />
              <h3 className="font-bold mb-2">Vendas seguem no Wix</h3>
              <p className="text-sm text-muted-foreground">
                Subdomínios de produto continuam intactos. Eduzz não pode parar.
              </p>
            </div>
            <div className="rounded-2xl bg-background/40 border border-border p-5">
              <Activity className="w-6 h-6 text-primary mb-3" />
              <h3 className="font-bold mb-2">Tracking preservado</h3>
              <p className="text-sm text-muted-foreground">
                GTM, Meta Pixel, Google Ads, GA4 e RD Station funcionando em todas as páginas novas.
              </p>
            </div>
          </div>
        </Slide>

        {/* Slide 2 — Janela */}
        <Slide number={2} total={total} kicker="Quando · Executado" title="Janela: madrugada de domingo para segunda" tone="success">
          <p className="text-base md:text-lg mb-4">
            Virada realizada no horário de menor tráfego para minimizar impacto caso houvesse
            qualquer instabilidade momentânea de SSL. <strong className="text-success">SSL provisionou sem
            interrupção e nenhuma venda foi perdida.</strong>
          </p>
          <div className="rounded-2xl bg-background/50 border border-success/30 p-5">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold mb-1">Por que essa janela importou</p>
                <p className="text-sm text-muted-foreground">
                  Na migração anterior, um problema de certificado SSL chegou a travar vendas da
                  Eduzz. Fazendo de madrugada, ganhamos folga pra ajuste fino sem prejudicar
                  conversões — e dessa vez funcionou no primeiro try.
                </p>
              </div>
            </div>
          </div>
        </Slide>

        {/* Slide 3 — Fase 1 concluída */}
        <Slide number={3} total={total} kicker="Fase 1 — Concluída" title="Preparação do projeto" tone="success">
          <ul className="space-y-3">
            {[
              { label: "GTM", id: "GTM-PR745X8V", note: "Google Analytics e Google Ads via container" },
              { label: "Meta Pixel", id: "2367275056629516", note: "Com fallback <noscript>" },
              { label: "RD Station", id: "loader 93e273bd…", note: "Carregando em todas as páginas" },
              { label: "Páginas", id: "institucional, congresso, leads", note: "Todas já criadas no Lovable" },
              { label: "LGPD — Cookies", id: "CookieConsent v1", note: "Banner minimalista próprio (RD Station desativado). Aceite válido por 365d, oculto no preview Lovable." },
              { label: "Área /admin — blindagem", id: "AdminGate + RLS + robots.txt", note: "Login + verificação de role 'admin' via has_role (SECURITY DEFINER). Todas as rotas /admin com noindex,nofollow. robots.txt bloqueia /admin e /api/admin. RLS no banco restringe leads/sales/contexto/config a admins." },
            ].map((item) => (
              <li key={item.label} className="flex items-start gap-3 rounded-xl bg-background/40 border border-success/20 p-4">
                <CheckCircle2 className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="font-bold">{item.label}</span>
                    <code className="text-xs text-primary">{item.id}</code>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{item.note}</p>
                </div>
              </li>
            ))}
          </ul>

          {/* Auth Settings do Cloud — confirmado */}
          <div className="mt-5 rounded-xl border border-success/30 bg-success/[0.06] p-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold">Auth Settings do Cloud — confirmado em produção</span>
                  <span className="text-[10px] uppercase tracking-widest text-success border border-success/40 rounded px-1.5 py-0.5">validado</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Conferido em <strong>Cloud → Users → Auth Settings</strong>:
                </p>
                <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <li>• <strong className="text-foreground">"Allow new users to sign up"</strong> — desativado (impede cadastro público no admin).</li>
                  <li>• <strong className="text-foreground">"Enable anonymous users"</strong> — desativado.</li>
                  <li>• <strong className="text-foreground">"Secure email change"</strong> — ativado (confirma email antigo e novo).</li>
                  <li>• <strong className="text-foreground">Password HIBP Check</strong> — ativado (bloqueia senhas vazadas via Have I Been Pwned).</li>
                </ul>
              </div>
            </div>
          </div>

        </Slide>

        {/* Slide 4 — Fase 2 */}
        <Slide number={4} total={total} kicker="Fase 2 — Concluída" title="Conexão do domínio (executado ao vivo)" tone="success">
          <p className="text-sm text-muted-foreground mb-4">
            Sequência seguida na virada — registro a registro foi revisado antes de aplicar,
            sem apagar nada da zona DNS do Wix/Eduzz/RD/email.
          </p>
          <ol className="space-y-3">
            {[
              "Backup: print da zona DNS completa no Registro.br",
              "No Lovable: Project Settings → Domains → Connect Domain",
              "Digitar fisioemortopedia.com.br e fazer login pelo Registro.br",
              "Autorizar acesso à zona DNS",
              "Revisão item-por-item da lista de registros propostos pela integração",
              "Aplicação confirmada após revisão (somente @ e www tocados)",
              "Repetido o processo para www.fisioemortopedia.com.br",
              "SSL provisionado automaticamente e domínio ficou Active",
            ].map((step, i) => (
              <li key={i} className="flex gap-4 items-start rounded-xl bg-background/40 border border-success/30 p-4">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-success/20 text-success font-bold flex items-center justify-center text-sm">
                  <CheckCircle2 className="w-4 h-4" />
                </span>
                <span className="pt-1">{step}</span>
              </li>
            ))}
          </ol>
        </Slide>

        {/* Slide 5 — O que aplicar */}
        <Slide number={5} total={total} kicker="DNS — o que mexer" title="Apenas estes 3 registros serão alterados/criados" tone="success">
          <div className="space-y-3">
            {[
              { type: "A", name: "@", value: "185.158.133.1", note: "Substitui o A do Wix do domínio raiz" },
              { type: "A", name: "www", value: "185.158.133.1", note: "Substitui o A/CNAME do www do Wix" },
              { type: "TXT", name: "_lovable", value: "lovable_verify=...", note: "Verificação de propriedade" },
            ].map((r) => (
              <div key={r.name} className="rounded-xl bg-background/50 border border-success/30 p-4">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <Pill tone="success">{r.type}</Pill>
                  <code className="text-primary font-mono text-sm">{r.name}</code>
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  <code className="text-foreground font-mono text-sm">{r.value}</code>
                </div>
                <p className="text-sm text-muted-foreground">{r.note}</p>
              </div>
            ))}
          </div>
        </Slide>

        {/* Slide 6 — O que NÃO mexer */}
        <Slide number={6} total={total} kicker="DNS — não tocar" title="Registros que precisam ficar 100% intactos" tone="danger">
          <p className="mb-4 text-base">
            Se a integração oferecer "limpar registros conflitantes", <strong className="text-destructive">recuse</strong>.
            Estes precisam continuar exatamente como estão:
          </p>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              { icon: ShoppingCart, label: "produto1, produto2, ...", note: "Páginas de venda no Wix (Eduzz)", critical: true },
              { icon: Activity, label: "materiais.fisioemortopedia.com.br", note: "RD Station — landing pages", critical: true },
              { icon: Shield, label: "pixel.fisioemortopedia.com.br", note: "Verificação de pixel / tracking", critical: true },
              { icon: Mail, label: "MX (todos)", note: "Recebimento de email", critical: true },
              { icon: Lock, label: "TXT SPF / DKIM / DMARC", note: "Autenticação de email", critical: true },
              { icon: Globe, label: "Qualquer outro CNAME/A", note: "Na dúvida → mantém", critical: false },
            ].map((item) => (
              <div key={item.label} className="rounded-xl bg-background/50 border border-destructive/25 p-4">
                <div className="flex items-start gap-3">
                  <item.icon className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-sm">{item.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{item.note}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 rounded-xl bg-destructive/10 border border-destructive/40 p-4 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
            <p className="text-sm">
              <strong>Antes de confirmar</strong> qualquer aplicação de registros no Lovable,
              cola aqui no chat a lista que aparecer. Eu reviso item por item.
            </p>
          </div>
        </Slide>

        {/* Slide 7 — Limpeza do projeto */}
        <Slide number={7} total={total} kicker="Pós-migração · Concluído" title="Liberar espaço na zona DNS" tone="success">
          <div className="space-y-4">
            <div className="rounded-xl bg-background/50 border border-success/30 p-5">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-bold mb-1">
                    Subdomínio <code className="text-primary">projeto.fisioemortopedia.com.br</code> mantido como redirect
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Após o domínio raiz ficar Active, optamos por <strong>manter</strong> o subdomínio configurado
                    como redirect 301 para o domínio principal — preserva links históricos que já circularam e
                    qualquer indexação antiga sem custo extra de zona DNS.
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-xl bg-background/50 border border-border p-4 text-sm">
              <p className="font-semibold mb-2">Estado final dos hostnames no projeto Lovable:</p>
              <ul className="space-y-1 text-muted-foreground list-disc list-inside">
                <li><code>fisioemortopedia.com.br</code> — <strong className="text-success">Primary</strong></li>
                <li><code>www.fisioemortopedia.com.br</code> → redirect pro primary</li>
                <li><code>projeto.fisioemortopedia.com.br</code> → redirect pro primary</li>
              </ul>
            </div>
          </div>
        </Slide>

        {/* Slide 8 — Webhook Eduzz */}
        <Slide number={8} total={total} kicker="Pós-migração · Concluído" title="Webhook da Eduzz conectado ao novo domínio" tone="success">
          <p className="mb-4 text-base">
            Webhook configurado no painel da Eduzz após o domínio ficar Active. <strong className="text-success">21 eventos de teste
            já chegaram com HTTP 200</strong> e foram gravados na tabela <code className="text-primary">sales</code> — autenticação
            por <code className="text-primary">EDUZZ_POSTBACK_TOKEN</code> validada de ponta a ponta.
          </p>
          <div className="rounded-xl bg-background/50 border border-primary/30 p-4 mb-4">
            <p className="text-xs uppercase tracking-wider text-primary font-bold mb-2">URL configurada no painel da Eduzz</p>
            <code className="block text-xs md:text-sm bg-background/70 p-3 rounded border border-border break-all">
              https://fisioemortopedia.com.br/api/public/hooks/eduzz?token=&lt;EDUZZ_POSTBACK_TOKEN&gt;
            </code>
            <p className="text-[11px] text-muted-foreground mt-2">
              Token real fica em <strong>Cloud → Secrets</strong>. Nunca cole o valor real no código —
              quem ler o source-code poderia forjar postbacks de venda.
            </p>
          </div>
          <ol className="space-y-3">
            {[
              "Eduzz → Avançado → Webhooks → Adicionar configuração",
              "Nome: [TR] Lovable · Paralelismo: 10",
              "Cola da URL acima → Verificar URL → 200 OK",
              "19 eventos selecionados: vendas (paga, gerada, boleto, pix), reembolso, chargeback, contratos",
              "Webhook do Stape (server.fisioemortopedia.com.br) mantido em paralelo — não foi tocado",
              "Configuração criada e validada com eventos de teste da Eduzz",
              "Eventos chegando em /admin/sales com latência <1min",
            ].map((step, i) => (
              <li key={i} className="flex gap-4 items-start rounded-xl bg-background/40 border border-success/30 p-4">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-success/20 text-success font-bold flex items-center justify-center text-sm">
                  <CheckCircle2 className="w-4 h-4" />
                </span>
                <span className="pt-1 text-sm">{step}</span>
              </li>
            ))}
          </ol>
        </Slide>

        {/* Slide 9 — Aviso de cookies LGPD próprio */}
        <Slide number={9} total={total} kicker="Pós-migração · LGPD · Concluído" title="Substituir aviso de cookies do RD Station por um próprio" tone="success">
          <p className="mb-4 text-sm md:text-base">
            Hoje o aviso de cookies aparece via código de monitoramento do RD Station —
            visual datado, sem controle de estilo e separado das nossas políticas. Subindo
            o domínio principal pro Lovable, ganhamos um banner próprio: discreto, alinhado
            ao design do site e linkado direto na nossa{" "}
            <a href="/politica-de-privacidade" className="text-primary underline">
              política de privacidade
            </a>.
          </p>
          <div className="grid md:grid-cols-2 gap-3 mb-4">
            <div className="rounded-xl bg-background/50 border border-success/30 p-4">
              <p className="text-xs uppercase tracking-wider text-success font-bold mb-2">
                Como vai ficar
              </p>
              <ul className="space-y-1.5 text-sm">
                <li>• Banner pequeno, canto inferior esquerdo, fundo do tema</li>
                <li>• Texto curto + botão "Ok, entendi" + link "Saiba mais"</li>
                <li>• Aparece 1x, lembra do consentimento por 1 ano (localStorage)</li>
                <li>• Componente <code className="text-xs bg-background/60 px-1.5 py-0.5 rounded">&lt;CookieConsent /&gt;</code> montado no <code className="text-xs bg-background/60 px-1.5 py-0.5 rounded">__root.tsx</code> — funciona em todas as páginas</li>
              </ul>
            </div>
            <div className="rounded-xl bg-background/50 border border-border p-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-2">
                Conformidade LGPD (base)
              </p>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                <li>• Informa o uso de cookies em destaque</li>
                <li>• Aponta pra Política de Privacidade já publicada</li>
                <li>• Permite continuar navegando sem clique forçado</li>
                <li>• Próximo nível (granular: aceitar / recusar marketing): só se quisermos endurecer depois</li>
              </ul>
            </div>
          </div>
          <div className="rounded-xl bg-destructive/10 border border-destructive/40 p-4 flex gap-3">
            <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold mb-1">Só desativar no RD Station depois que validar</p>
              <p className="text-muted-foreground">
                Subir o banner próprio com a migração → checar nas páginas principais → aí sim entrar no
                RD Station Marketing e desligar a "Política de cookies" pra não exibir dois avisos sobrepostos.
              </p>
            </div>
          </div>
        </Slide>

        {/* Slide 10 — Pendências pós-virada (todas concluídas) */}
        <Slide number={10} total={total} kicker="Pós-virada · Concluído" title="Pendências manuais — todas resolvidas" tone="success">
          <ul className="space-y-3">
            <li className="flex items-start gap-3 rounded-xl bg-background/40 border border-success/30 p-4">
              <CheckCircle2 className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-bold mb-1">Google Search Console — propriedade ativa</p>
                <ol className="text-sm text-muted-foreground space-y-1 list-decimal ml-5">
                  <li>Propriedade <code className="text-xs">https://fisioemortopedia.com.br</code> verificada (método: Tag HTML).</li>
                  <li>Sitemap enviado: <code className="text-xs bg-background/60 px-1.5 py-0.5 rounded">sitemap.xml</code> (16 URLs públicas).</li>
                  <li>Histórico já visível no painel: <strong className="text-success">8.259 cliques</strong> acumulados, 37 páginas indexadas e 85 sob análise.</li>
                  <li>Acesso programático via conexão Google OAuth do Lovable — sem precisar adicionar usuário extra.</li>
                </ol>
              </div>
            </li>
            <li className="flex items-start gap-3 rounded-xl bg-background/40 border border-success/30 p-4">
              <CheckCircle2 className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-bold mb-1">Eduzz — postback ativo e validado</p>
                <p className="text-sm text-muted-foreground">
                  URL <code className="text-xs">/api/public/hooks/eduzz</code> apontada para o domínio principal, com
                  <code className="text-xs mx-1">EDUZZ_POSTBACK_TOKEN</code> no query-string. <strong>21 eventos de teste</strong>
                  chegaram com HTTP 200 e gravaram em <code className="text-xs">sales</code>.
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3 rounded-xl bg-background/40 border border-success/30 p-4">
              <CheckCircle2 className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-bold mb-1">RD Station — política de cookies desligada</p>
                <p className="text-sm text-muted-foreground">
                  Banner LGPD próprio validado em produção, e a "Política de cookies" do RD Station foi desativada
                  para não exibir dois avisos sobrepostos.
                </p>
              </div>
            </li>
          </ul>
        </Slide>


        {/* Slide 10 — Testes */}
        <Slide number={11} total={total} kicker="Fase 3 · Concluído" title="Checklist de testes pós-virada" tone="success">
          <p className="mb-4 text-sm text-muted-foreground">Todos os itens abaixo foram validados após o domínio entrar em <Pill tone="success">Active</Pill>:</p>

          <div className="grid md:grid-cols-2 gap-3">
            {[
              { icon: Lock, label: "HTTPS abrindo sem aviso", scope: "raiz + www + projeto. (redirects 301)" },
              { icon: ShoppingCart, label: "Link da Eduzz funcionando", scope: "checkouts abrindo a partir do site novo" },
              { icon: ShoppingCart, label: "Subdomínios pos-*.fisioemortopedia.com.br", scope: "checkout completo no Wix preservado" },
              { icon: Mail, label: "Formulário RD Station", scope: "enviando lead normalmente" },
              { icon: Activity, label: "Meta Pixel", scope: "PageView confirmado em Test Events" },
              { icon: Activity, label: "Google Ads", scope: "conversão via GTM Preview OK" },
              { icon: Activity, label: "GA4", scope: "sessões em tempo real" },
              { icon: Mail, label: "Email corporativo", scope: "envio + recebimento intactos" },
              { icon: Globe, label: "materiais. + pixel.", scope: "subdomínios externos respondendo" },
            ].map((t, i) => (
              <div
                key={i}
                className="flex items-start gap-3 rounded-xl bg-background/40 border border-success/30 p-3"
              >
                <CheckCircle2 className="mt-0.5 w-5 h-5 text-success flex-shrink-0" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <t.icon className="w-4 h-4 text-primary" />
                    <span className="font-semibold text-sm">{t.label}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 ml-6">{t.scope}</p>
                </div>
              </div>
            ))}
          </div>
        </Slide>

        {/* Slide 11 — Riscos & Mitigação */}
        <Slide number={12} total={total} kicker="Resumo de riscos" title="O que pode dar errado e como mitigamos">
          <div className="space-y-3">
            {[
              {
                risk: "SSL demorar a emitir e travar Eduzz",
                fix: "Virada em madrugada + link Eduzz como backup divulgável",
                icon: Lock,
              },
              {
                risk: "Integração apagar registros do Wix/RD/email",
                fix: "Revisão obrigatória da lista de alterações antes de confirmar",
                icon: XCircle,
              },
              {
                risk: "Subdomínios de produto pararem (Eduzz)",
                fix: "Nenhum CNAME/A de subdomínio é tocado — só @ e www",
                icon: ShoppingCart,
              },
              {
                risk: "Email parar de funcionar",
                fix: "MX, SPF, DKIM e DMARC ficam intactos",
                icon: Mail,
              },
              {
                risk: "Conversões / leads pararem de trackear",
                fix: "Tracking já validado no __root.tsx, carrega em todas as páginas",
                icon: Activity,
              },
            ].map((r, i) => (
              <div key={i} className="rounded-xl bg-background/40 border border-border p-4 grid md:grid-cols-[auto_1fr_1fr] gap-3 items-start">
                <r.icon className="w-5 h-5 text-gold mt-0.5" />
                <div>
                  <p className="text-xs uppercase tracking-wider text-destructive font-bold mb-1">Risco</p>
                  <p className="text-sm">{r.risk}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-success font-bold mb-1">Mitigação</p>
                  <p className="text-sm">{r.fix}</p>
                </div>
              </div>
            ))}
          </div>
        </Slide>

        {/* Slide 12 — Mapa de URLs (referência rápida da equipe) */}
        <Slide
          number={13}
          total={total}
          kicker="Referência rápida"
          title="Links oficiais — linha de frente da empresa"
          tone="success"
        >
          <p className="mb-2 text-sm md:text-base text-foreground">
            Estes são os <strong>únicos links oficiais</strong> em uso após a migração. Clique para abrir
            ou no ícone <Copy className="inline w-3.5 h-3.5 -mt-0.5" /> para copiar.
          </p>
          <p className="mb-6 text-xs text-muted-foreground">
            Sempre que precisar divulgar uma página da empresa, use exatamente estes endereços —
            não use mais nenhum subdomínio antigo do Wix para essas páginas.
          </p>

          <LinksGrupos grupos={GRUPOS_INSTITUCIONAIS} />


          <div className="mt-6 rounded-xl bg-gold/10 border border-gold/30 p-4 flex gap-3">
            <ShoppingCart className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
            <p className="text-sm">
              <strong>Páginas de venda das pós-graduações</strong> (Orto, Dor, Esporte, Geronto, Trauma, Prescrição)
              continuam nos subdomínios <code className="text-xs bg-background/50 px-1.5 py-0.5 rounded">pos-*.fisioemortopedia.com.br</code> no
              Wix. A reconstrução delas é a{" "}
              <a href="/admin/plano-paginas" className="text-primary underline">Fase 2 — /plano-paginas</a>.
            </p>
          </div>
        </Slide>

        {/* Slide 14 — Resumo executivo para a equipe */}
        <Slide number={14} total={total} kicker="Encerramento" title="Resumo executivo · O que foi entregue" tone="success">
          <p className="mb-6 text-base">
            Migração completa do domínio principal <code className="text-primary">fisioemortopedia.com.br</code> do
            Wix para o Lovable, sem perda de vendas, sem queda de email, sem perda de tracking — e com diversos
            ganhos colaterais que o site antigo não tinha.
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="rounded-xl bg-background/50 border border-success/30 p-4">
              <p className="text-xs uppercase tracking-wider text-success font-bold mb-3">Infraestrutura</p>
              <ul className="space-y-1.5 text-sm">
                <li>✅ Domínio raiz + www + projeto. servindo via Lovable</li>
                <li>✅ SSL automático, renovação gerenciada</li>
                <li>✅ Zona DNS preservada (Eduzz, RD, email intactos)</li>
                <li>✅ Sitemap dinâmico em <code className="text-xs">/sitemap.xml</code></li>
                <li>✅ robots.txt bloqueando <code className="text-xs">/admin</code> e <code className="text-xs">/api/admin</code></li>
              </ul>
            </div>
            <div className="rounded-xl bg-background/50 border border-success/30 p-4">
              <p className="text-xs uppercase tracking-wider text-success font-bold mb-3">Tracking & Vendas</p>
              <ul className="space-y-1.5 text-sm">
                <li>✅ GTM (GTM-PR745X8V) carregando em todas as páginas</li>
                <li>✅ Meta Pixel (2367275056629516) com fallback noscript</li>
                <li>✅ Google Ads + GA4 via container GTM</li>
                <li>✅ RD Station loader carregando em todas as páginas</li>
                <li>✅ Webhook Eduzz gravando vendas em <code className="text-xs">sales</code></li>
              </ul>
            </div>
            <div className="rounded-xl bg-background/50 border border-success/30 p-4">
              <p className="text-xs uppercase tracking-wider text-success font-bold mb-3">SEO & Indexação</p>
              <ul className="space-y-1.5 text-sm">
                <li>✅ Google Search Console verificado</li>
                <li>✅ Sitemap enviado (16 URLs públicas)</li>
                <li>✅ Histórico preservado: 8.259 cliques acumulados</li>
                <li>✅ 37 páginas indexadas, 85 sob análise</li>
                <li>✅ Meta-tags, canonical e og:* por rota</li>
              </ul>
            </div>
            <div className="rounded-xl bg-background/50 border border-success/30 p-4">
              <p className="text-xs uppercase tracking-wider text-success font-bold mb-3">Segurança & LGPD</p>
              <ul className="space-y-1.5 text-sm">
                <li>✅ <code className="text-xs">/admin</code> blindada (AdminGate + RLS + has_role)</li>
                <li>✅ Banner LGPD próprio (CookieConsent, 365d)</li>
                <li>✅ RD Station "Política de cookies" desligada</li>
                <li>✅ Token do webhook em Secrets (nunca no código)</li>
                <li>✅ RLS no banco: leads/sales só admins leem</li>
              </ul>
            </div>
          </div>

          <div className="rounded-xl bg-success/[0.06] border border-success/30 p-4 mb-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-bold text-sm mb-1">Hardening do painel /admin concluído</p>
                <p className="text-sm text-muted-foreground">
                  Em <strong>Cloud → Users → Auth Settings</strong>: "Allow new users to sign up" <strong>desativado</strong>,
                  "Enable anonymous users" <strong>desativado</strong>, "Secure email change" <strong>ativado</strong> e
                  "Password HIBP Check" <strong>ativado</strong> (bloqueia senhas que já vazaram em bases públicas via Have I Been Pwned).
                  Combinado com AdminGate + RLS + <code className="text-xs">has_role</code>, o painel <code className="text-xs">/admin</code> está blindado.
                </p>
              </div>
            </div>
          </div>


          <div className="rounded-xl bg-background/50 border border-border p-4">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-2">Próxima fase (escopo separado)</p>
            <p className="text-sm">
              Reconstrução das <strong>páginas de venda das pós-graduações</strong> (Orto, Dor, Esporte, Geronto,
              Trauma, Prescrição) — hoje ainda nos subdomínios <code className="text-xs">pos-*.fisioemortopedia.com.br</code> no
              Wix. Tratado em{" "}
              <a href="/admin/plano-paginas" className="text-primary underline">/admin/plano-paginas</a>.
            </p>
          </div>
        </Slide>




      </main>

      <footer className="border-t border-border mt-12">
        <div className="max-w-5xl mx-auto px-6 py-6 text-center text-xs text-muted-foreground">
          Documento interno · Migração concluída em Junho/2026 · Fisio em Ortopedia
        </div>
      </footer>
    </div>
  );
}
